import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getActiveSubscriptionOrNull } from '../utils/subscriptionApiUtils';
import { isUserAccountActive } from '../utils/accessUtils';

interface SubscriptionAccessValue {
  /** Идёт первичная проверка подписки (только для Public) */
  isLoading: boolean;
  /** У пользователя Public есть активная подписка (после успешной загрузки) */
  hasActiveSubscription: boolean;
  /** Public без подписки — доступны только экраны /subscription/* */
  mustPurchaseSubscription: boolean;
  /** GET /subscriptions/active-subscription (Bearer) — обновить кэш после оплаты / входа */
  refresh: () => Promise<void>;
}

const SubscriptionAccessContext = createContext<SubscriptionAccessValue | null>(null);

export const SubscriptionAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector((state: RootState) => state.user);
  const [isLoading, setIsLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const isPublicOrg = user?.organization === 'Public';
  const shouldCheck = !!(user && isUserAccountActive(user) && isPublicOrg);

  const refresh = useCallback(async () => {
    if (!user || !isUserAccountActive(user)) {
      setHasActiveSubscription(false);
      setIsLoading(false);
      return;
    }
    if (!isPublicOrg) {
      setHasActiveSubscription(true);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await getActiveSubscriptionOrNull();
      setHasActiveSubscription(!!data?.subscription);
    } catch {
      setHasActiveSubscription(false);
    } finally {
      setIsLoading(false);
    }
  }, [user, isPublicOrg]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SubscriptionAccessValue>(
    () => ({
      isLoading: shouldCheck ? isLoading : false,
      hasActiveSubscription: shouldCheck ? hasActiveSubscription : true,
      mustPurchaseSubscription: shouldCheck && !isLoading && !hasActiveSubscription,
      refresh,
    }),
    [shouldCheck, isLoading, hasActiveSubscription, refresh]
  );

  return (
    <SubscriptionAccessContext.Provider value={value}>{children}</SubscriptionAccessContext.Provider>
  );
};

export const useSubscriptionAccess = (): SubscriptionAccessValue => {
  const ctx = useContext(SubscriptionAccessContext);
  if (!ctx) {
    throw new Error('useSubscriptionAccess must be used within SubscriptionAccessProvider');
  }
  return ctx;
};
